import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
	Box,
	Typography,
	Paper,
	Stack,
	Button,
	Avatar
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { tokens } from "../../theme";
import CustomIcon from "../../components/icon";
import HomeSkeletonPage from "../../components/home-button/home-skeleton";

import { djangoApi } from "../../utils/axios/axios.utils";
import { setSellers } from "../../store/sellers/sellers.action";
import { selectSellers } from "../../store/sellers/sellers.selector";
import { getCatalogExportsQuery } from "../../utils/firebase/firebase.datatable";

const HomeButon = () => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const isNonMobile = useMediaQuery("(min-width: 900px)");
	const isXL = useMediaQuery("(min-width: 1536px)");
	const navigation = useNavigate();

	const dispatch = useDispatch();
	const sellersData = useSelector(selectSellers);

	const [isLoading, setIsLoading] = useState(true);
	const [catalogExports, setCatalogExports] = useState([]);

	const OPTIONS_BUTTONS = [
		{ label: "PIX", path: "pix" },
		{ label: "Cartão de Crédito", path: "credit-card" }
	];

	const hanlerPaymentNav = (type) => {
		navigation(`/${type}`);
	};

	useEffect(() => {
		const getSellersData = async () => {
			try {
				const res = await djangoApi.get("sellers/sellers/get_sellers", {
					headers: {
						Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
						"Content-Type": "application/json"
					}
				});

				const { data, failure_payments } = res.data;
				const kitsByUser = {};

				if (Array.isArray(failure_payments)) {
					failure_payments.forEach((kit) => {
						const sellerId = kit.seller__pk;
						if (!kitsByUser[sellerId]) {
							kitsByUser[sellerId] = [];
						}
						kitsByUser[sellerId].push({ ...kit });
					});
				}

				const unified = data.map((user) => ({
					...user,
					kits_inadimplentes: kitsByUser[user.id] || []
				}));

				dispatch(setSellers(unified));
			} catch (err) {
				console.log("Erro ao buscar sellers:", err);
			}
		};

		const loadAll = async () => {
			setIsLoading(true);
			try {
				if (!sellersData?.length) {
					await getSellersData();
				}

				const exportsData = await getCatalogExportsQuery(300);
				setCatalogExports(Array.isArray(exportsData) ? exportsData : []);
			} catch (error) {
				console.log("Erro ao carregar dashboard:", error);
			} finally {
				setIsLoading(false);
			}
		};

		loadAll();
	}, []);

	const safeDate = (value) => {
		if (!value) return null;

		if (value?.toDate && typeof value.toDate === "function") {
			return value.toDate();
		}

		if (value?.seconds) {
			return new Date(value.seconds * 1000);
		}

		if (value instanceof Date) {
			return value;
		}

		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	};

	const formatDateTimeBR = (value) => {
		const date = safeDate(value);
		if (!date) return "-";

		return new Intl.DateTimeFormat("pt-BR", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit"
		}).format(date);
	};

	const dashboardData = useMemo(() => {
		const exportsNormalized = (catalogExports || []).map((item) => {
			const createdAtDate = safeDate(item.createdAt);

			return {
				...item,
				createdAtDate,
				itemsCount: Number(item.itemsCount || 0),
				kitPk: item.kitPk ?? null,
				sellerName: item.sellerName || "Sem nome",
				sellerEmail: item.sellerEmail || "-",
				sellerId: item.sellerId || null
			};
		});

		const totalExports = exportsNormalized.length;

		const totalItemsExported = exportsNormalized.reduce((acc, item) => {
			return acc + Number(item.itemsCount || 0);
		}, 0);

		const uniqueSellerIds = new Set(
			exportsNormalized
				.map((item) => item.sellerId || item.sellerEmail || item.sellerName)
				.filter(Boolean)
		);

		const uniqueKits = new Set(
			exportsNormalized.map((item) => item.kitPk).filter(Boolean)
		);

		const avgItemsPerExport = totalExports
			? totalItemsExported / totalExports
			: 0;

		const sevenDaysAgo = new Date();
		sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
		sevenDaysAgo.setHours(0, 0, 0, 0);

		const exportsLast7Days = exportsNormalized.filter((item) => {
			if (!item.createdAtDate) return false;
			return item.createdAtDate >= sevenDaysAgo;
		}).length;

		const groupedByDayMap = {};
		for (let i = 6; i >= 0; i -= 1) {
			const date = new Date();
			date.setDate(date.getDate() - i);

			const key = date.toISOString().slice(0, 10);
			groupedByDayMap[key] = {
				key,
				label: new Intl.DateTimeFormat("pt-BR", {
					day: "2-digit",
					month: "2-digit"
				}).format(date),
				total: 0
			};
		}

		exportsNormalized.forEach((item) => {
			if (!item.createdAtDate) return;
			const key = item.createdAtDate.toISOString().slice(0, 10);
			if (groupedByDayMap[key]) {
				groupedByDayMap[key].total += 1;
			}
		});

		const chartLast7Days = Object.values(groupedByDayMap);

		const sellerMap = {};
		exportsNormalized.forEach((item) => {
			const key = item.sellerId || item.sellerEmail || item.sellerName;
			if (!key) return;

			if (!sellerMap[key]) {
				sellerMap[key] = {
					sellerName: item.sellerName || "Sem nome",
					sellerEmail: item.sellerEmail || "-",
					totalExports: 0,
					totalItems: 0
				};
			}

			sellerMap[key].totalExports += 1;
			sellerMap[key].totalItems += Number(item.itemsCount || 0);
		});

		const topSellers = Object.values(sellerMap).sort(
			(a, b) => b.totalExports - a.totalExports
		);

		const latestExports = [...exportsNormalized]
			.sort((a, b) => {
				const da = a.createdAtDate ? a.createdAtDate.getTime() : 0;
				const db = b.createdAtDate ? b.createdAtDate.getTime() : 0;
				return db - da;
			})
			.slice(0, 10);

		return {
			totalExports,
			totalItemsExported,
			totalSellers: uniqueSellerIds.size,
			totalUniqueKits: uniqueKits.size,
			avgItemsPerExport,
			exportsLast7Days,
			chartLast7Days,
			topSellers,
			latestExports
		};
	}, [catalogExports]);

	const maxBar = Math.max(...dashboardData.chartLast7Days.map((d) => d.total), 1);

	if (isLoading) {
		return (
			<Box
				sx={{
					width: "100%",
					minHeight: "100vh",
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "center",
					pt: "110px",
					px: 2,
					boxSizing: "border-box"
				}}
			>
				<HomeSkeletonPage />
			</Box>
		);
	}

	const mainGap = isXL ? 2.2 : isNonMobile ? 2 : 1.5;

	const GlassCard = ({ children, sx = {} }) => (
		<Paper
			elevation={0}
			sx={{
				height: "100%",
				borderRadius: "26px",
				p: isNonMobile ? 3 : 2.2,
				background:
					theme.palette.mode === "dark"
						? "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)"
						: "linear-gradient(180deg, rgba(255,255,255,0.995) 0%, rgba(248,250,252,0.98) 100%)",
				border:
					theme.palette.mode === "dark"
						? "1px solid rgba(255,255,255,0.05)"
						: "1px solid rgba(15,23,42,0.05)",
				boxShadow:
					theme.palette.mode === "dark"
						? "0 10px 24px rgba(0,0,0,0.10)"
						: "0 10px 26px rgba(15,23,42,0.05)",
				backdropFilter: "blur(10px)",
				boxSizing: "border-box",
				...sx
			}}
		>
			{children}
		</Paper>
	);

	const KpiCard = ({ title, value, subtitle, icon }) => (
		<GlassCard sx={{ minHeight: 165 }}>
			<Stack spacing={1.5} sx={{ height: "100%", justifyContent: "space-between" }}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
					<Box sx={{ pr: 2 }}>
						<Typography
							sx={{
								fontSize: "0.95rem",
								fontWeight: 700,
								color: colors.grey[300]
							}}
						>
							{title}
						</Typography>
					</Box>

					<Box
						sx={{
							width: 46,
							height: 46,
							minWidth: 46,
							borderRadius: "15px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background:
								theme.palette.mode === "dark"
									? "rgba(255,255,255,0.05)"
									: "rgba(15,23,42,0.05)",
							fontSize: "20px"
						}}
					>
						{icon}
					</Box>
				</Stack>

				<Box>
					<Typography
						sx={{
							fontSize: isNonMobile ? "2.15rem" : "1.8rem",
							lineHeight: 1,
							fontWeight: 900,
							letterSpacing: "-0.03em",
							color: colors.grey[100]
						}}
					>
						{value}
					</Typography>

					{!!subtitle && (
						<Typography
							sx={{
								mt: 1.25,
								fontSize: "0.92rem",
								lineHeight: 1.45,
								color: colors.grey[400]
							}}
						>
							{subtitle}
						</Typography>
					)}
				</Box>
			</Stack>
		</GlassCard>
	);

	return (
		<Box
			sx={{
				width: "100%",
				minHeight: "100vh",
				boxSizing: "border-box",
				pt: isNonMobile ? "60px" : "60px",
				pb: isNonMobile ? 5 : 3,
				px: isNonMobile ? 4 : 2,
				background:
					theme.palette.mode === "dark"
						? "linear-gradient(180deg, #232833 0%, #2a303b 100%)"
						: "linear-gradient(180deg, #fcfdff 0%, #f5f8fc 100%)",
				overflowX: "hidden"
			}}
		>
			<Box
				sx={{
					width: "100%",
					maxWidth: "1680px",
					mx: "auto"
				}}
			>
				<Stack
					direction={isNonMobile ? "row" : "column"}
					justifyContent="space-between"
					alignItems={isNonMobile ? "flex-end" : "flex-start"}
					spacing={3}
					sx={{ mb: 4.2 }}
				>
					<Box sx={{ maxWidth: 1260 }}>
						<Typography
							sx={{
								fontSize: isNonMobile ? "2.7rem" : "2.05rem",
								fontWeight: 900,
								letterSpacing: "-0.04em",
								lineHeight: 1.05,
								color: colors.grey[100]
							}}
						>
							Dashboard inicial
						</Typography>

						<Typography
							sx={{
								mt: 1.1,
								fontSize: isNonMobile ? "1.02rem" : "0.95rem",
								lineHeight: 1.65,
								color: colors.grey[400],
								maxWidth: 1560
							}}
						>
							Painel inicial com métricas de exportação de catálogo, leitura rápida
							de atividade e ranking operacional para apoiar a evolução do sistema.
						</Typography>
					</Box>

				</Stack>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2, minmax(0, 1fr))",
							xl: "repeat(4, minmax(0, 1fr))"
						},
						gap: mainGap
					}}
				>
					<KpiCard
						title="Total de exports"
						value={dashboardData.totalExports}
						subtitle="Todos os registros encontrados em catalog_exports"
						icon="📦"
					/>

					<KpiCard
						title="Últimos 7 dias"
						value={dashboardData.exportsLast7Days}
						subtitle="Atividade recente de exportação"
						icon="📈"
					/>

					<KpiCard
						title="Vendedoras ativas"
						value={dashboardData.totalSellers}
						subtitle="SellerId, e-mail ou nome únicos"
						icon="👥"
					/>

					<KpiCard
						title="Média de itens/export"
						value={dashboardData.avgItemsPerExport.toFixed(1)}
						subtitle={`Itens exportados no total: ${dashboardData.totalItemsExported}`}
						icon="🧾"
					/>
				</Box>

				<Box
					sx={{
						mt: mainGap,
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							xl: "minmax(0, 1.7fr) minmax(360px, 0.95fr)"
						},
						gap: mainGap,
						alignItems: "stretch"
					}}
				>
					<GlassCard>
						<Stack spacing={3}>
							<Box>
								<Typography
									sx={{
										fontSize: "1.14rem",
										fontWeight: 900,
										letterSpacing: "-0.02em",
										color: colors.grey[100]
									}}
								>
									Exportações por dia
								</Typography>

								<Typography
									sx={{
										mt: 0.8,
										fontSize: "0.94rem",
										lineHeight: 1.55,
										color: colors.grey[400]
									}}
								>
									Distribuição dos últimos 7 dias para leitura rápida de movimento
									no catálogo.
								</Typography>
							</Box>

							<Box
								sx={{
									height: 290,
									display: "flex",
									alignItems: "flex-end",
									justifyContent: "space-between",
									gap: isNonMobile ? 1.8 : 0.9,
									pt: 1,
									px: isNonMobile ? 1 : 0
								}}
							>
								{dashboardData.chartLast7Days.map((item) => {
									const height = `${Math.max(
										18,
										(item.total / maxBar) * 100
									)}%`;

									return (
										<Box
											key={item.key}
											sx={{
												flex: 1,
												minWidth: 0,
												height: "100%",
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												justifyContent: "flex-end"
											}}
										>
											<Typography
												sx={{
													mb: 1.05,
													fontSize: "0.86rem",
													fontWeight: 800,
													color: colors.grey[300]
												}}
											>
												{item.total}
											</Typography>

											<Box
												sx={{
													width: "100%",
													maxWidth: isNonMobile ? 64 : 48,
													height,
													borderRadius: "20px",
													background:
														theme.palette.mode === "dark"
															? "linear-gradient(180deg, #f8b4d9 0%, #ec4899 100%)"
															: "linear-gradient(180deg, #4b5563 0%, #111827 100%)",
													boxShadow:
														theme.palette.mode === "dark"
															? "0 10px 20px rgba(236,72,153,0.16)"
															: "0 10px 22px rgba(15,23,42,0.10)",
													transition: "0.25s ease"
												}}
											/>

											<Typography
												sx={{
													mt: 1.2,
													fontSize: "0.84rem",
													color: colors.grey[400]
												}}
											>
												{item.label}
											</Typography>
										</Box>
									);
								})}
							</Box>
						</Stack>
					</GlassCard>

					<GlassCard>
						<Stack spacing={2.5} sx={{ height: "100%" }}>
							<Box>
								<Typography
									sx={{
										fontSize: "1.14rem",
										fontWeight: 900,
										letterSpacing: "-0.02em",
										color: colors.grey[100]
									}}
								>
									Top vendedoras
								</Typography>

								<Typography
									sx={{
										mt: 0.8,
										fontSize: "0.94rem",
										lineHeight: 1.55,
										color: colors.grey[400]
									}}
								>
									Ranking por quantidade de exportações registradas.
								</Typography>
							</Box>

							<Box
								sx={{
									maxHeight: 505,
									overflowY: "auto",
									pr: 0.5,
									mr: -0.4,
									display: "flex",
									flexDirection: "column",
									gap: 1.2,
									"&::-webkit-scrollbar": {
										width: "8px"
									},
									"&::-webkit-scrollbar-thumb": {
										background:
											theme.palette.mode === "dark"
												? "rgba(255,255,255,0.10)"
												: "rgba(15,23,42,0.12)",
										borderRadius: "999px"
									},
									"&::-webkit-scrollbar-track": {
										background: "transparent"
									}
								}}
							>
								{dashboardData.topSellers.length === 0 ? (
									<Typography sx={{ color: colors.grey[400] }}>
										Não há exportações suficientes para ranking.
									</Typography>
								) : (
									dashboardData.topSellers.map((seller, index) => (
										<Box
											key={`${seller.sellerEmail}-${index}`}
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												gap: 2,
												p: 1.4,
												borderRadius: "18px",
												background:
													theme.palette.mode === "dark"
														? "rgba(255,255,255,0.03)"
														: "rgba(255,255,255,0.86)",
												border:
													theme.palette.mode === "dark"
														? "1px solid rgba(255,255,255,0.04)"
														: "1px solid rgba(15,23,42,0.04)"
											}}
										>
											<Stack
												direction="row"
												spacing={1.3}
												alignItems="center"
												sx={{ minWidth: 0, flex: 1 }}
											>
												<Avatar
													sx={{
														width: 40,
														height: 40,
														fontSize: "0.95rem",
														fontWeight: 800,
														bgcolor:
															theme.palette.mode === "dark"
																? "rgba(255,255,255,0.08)"
																: "rgba(17,24,39,0.08)",
														color: colors.grey[100]
													}}
												>
													{index + 1}
												</Avatar>

												<Box sx={{ minWidth: 0 }}>
													<Typography
														noWrap
														sx={{
															fontWeight: 800,
															color: colors.grey[100]
														}}
													>
														{seller.sellerName}
													</Typography>
													<Typography
														noWrap
														sx={{
															fontSize: "0.84rem",
															color: colors.grey[400]
														}}
													>
														{seller.sellerEmail}
													</Typography>
												</Box>
											</Stack>

											<Box textAlign="right" sx={{ minWidth: 74 }}>
												<Typography
													sx={{
														fontSize: "1.05rem",
														fontWeight: 900,
														color: colors.grey[100]
													}}
												>
													{seller.totalExports}
												</Typography>
												<Typography
													sx={{
														fontSize: "0.8rem",
														color: colors.grey[400]
													}}
												>
													exports
												</Typography>
											</Box>
										</Box>
									))
								)}
							</Box>
						</Stack>
					</GlassCard>
				</Box>

				<Box sx={{ mt: mainGap }}>
					<GlassCard>
						<Stack spacing={2.6}>
							<Box>
								<Typography
									sx={{
										fontSize: "1.14rem",
										fontWeight: 900,
										letterSpacing: "-0.02em",
										color: colors.grey[100]
									}}
								>
									Últimas exportações
								</Typography>

								<Typography
									sx={{
										mt: 0.8,
										fontSize: "0.94rem",
										lineHeight: 1.55,
										color: colors.grey[400]
									}}
								>
									Leitura operacional das movimentações mais recentes.
								</Typography>
							</Box>

							<Box sx={{ overflowX: "auto", width: "100%" }}>
								<Box sx={{ minWidth: 980 }}>
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: "1.35fr 1.45fr 0.8fr 0.7fr 1fr",
											columnGap: 2.1,
											rowGap: 0,
											px: 1.2,
											pb: 1.1
										}}
									>
										{["Vendedora", "E-mail", "Kit", "Itens", "Data"].map((label) => (
											<Typography
												key={label}
												sx={{
													fontSize: "0.8rem",
													fontWeight: 900,
													textTransform: "uppercase",
													letterSpacing: "0.08em",
													color: colors.grey[400]
												}}
											>
												{label}
											</Typography>
										))}
									</Box>

									<Stack spacing={1.15}>
										{dashboardData.latestExports.map((row) => (
											<Box
												key={row.id}
												sx={{
													display: "grid",
													gridTemplateColumns: "1.35fr 1.45fr 0.8fr 0.7fr 1fr",
													columnGap: 2.1,
													rowGap: 0,
													alignItems: "center",
													p: 1.35,
													borderRadius: "18px",
													background:
														theme.palette.mode === "dark"
															? "rgba(255,255,255,0.022)"
															: "rgba(255,255,255,0.88)",
													border:
														theme.palette.mode === "dark"
															? "1px solid rgba(255,255,255,0.04)"
															: "1px solid rgba(15,23,42,0.04)"
												}}
											>
												<Typography
													sx={{
														fontWeight: 800,
														color: colors.grey[100],
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap"
													}}
												>
													{row.sellerName || "-"}
												</Typography>

												<Typography
													sx={{
														color: colors.grey[300],
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap"
													}}
												>
													{row.sellerEmail || "-"}
												</Typography>

												<Typography
													sx={{
														fontWeight: 700,
														color: colors.grey[300],
														whiteSpace: "nowrap"
													}}
												>
													#{row.kitPk || "-"}
												</Typography>

												<Typography
													sx={{
														fontWeight: 900,
														color: colors.grey[100],
														whiteSpace: "nowrap"
													}}
												>
													{row.itemsCount || 0}
												</Typography>

												<Typography
													sx={{
														color: colors.grey[300],
														whiteSpace: "nowrap"
													}}
												>
													{formatDateTimeBR(row.createdAt)}
												</Typography>
											</Box>
										))}
									</Stack>
								</Box>
							</Box>
						</Stack>
					</GlassCard>
				</Box>
			</Box>
		</Box>
	);
};

export default HomeButon;